import { AuthenticationApi } from "bonusx-api-auth-manager";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { Account } from "bonusx-api-main-manager";
import { AbacGuardService } from "../";
import { subscribeToQuestionnairePersist } from "../../../../app/store/store";
import { authApiConfig_v1, LOGGED_HOME_PAGE_PATH, WELCOME_PATH } from "../../../../constants/env";
import { useIsLogged } from "../../../../hooks/auth/use-is-logged/use-is-logged";
import { externalLoginService } from "../../../../legacy/pages/Auth/external-login/services/external-login.service";
import { getNonceTokenFromQueryParams } from "../../../../legacy/pages/Auth/external-login/utils/get-nonce-token-from-query-params/get-nonce-token-from-query-params.util";
import { ONBOARDING_SURVEY_PATH } from "../../../../legacy/pages/survey/config/survey-paths";
import { addContextIdToPathOrDefault } from "../../../../legacy/pages/survey/utils/add-context-id-to-path";
import { authenticationServiceInstance } from "../../../../legacy/services/authentication-service/authentication.service.instance";
import { getDecodedUserAccessTokenFromBrowserCookies } from "../../../../legacy/services/authentication-service/stores/access-token/get-decoded-user-access-token-from-browser-cookies/get-decoded-user-access-token-from-browser-cookies";
import axiosInstance from "../../../../legacy/services/axios-config-singleton/axios-config-singleton.instance";
import { accountApi } from "../../../../legacy/services/main-account-api-instance";
import { questionnaireSubject$ } from "../../../../legacy/stores/questionnaireSubject";
import featureFlag from "../../../../lib/feature-flag/feature-flag";
import { registerTrackingConsentSync } from "../../../../lib/mixpanel/session-replay/session-replay";
import type { IAccountWithEligibility } from "../../../../types/account-with-eligibility.interface";
import { getSafeNextUrlPath } from "../utils/get-safe-next-url-path/get-safe-next-url-path";

interface IUseExternalLoginGuard {
  loading: boolean;
  redirectTo: string | null;
  error: unknown;
}

function resolveTargetPath(account: Account, nextUrlPath: string | null): string {
  if (nextUrlPath) {
    return nextUrlPath;
  }
  if (account.profileCompleted) {
    return LOGGED_HOME_PAGE_PATH;
  }
  return addContextIdToPathOrDefault(ONBOARDING_SURVEY_PATH);
}

function hasNonceToken(searchParams: URLSearchParams): boolean {
  return Boolean(getNonceTokenFromQueryParams(searchParams));
}

async function runLoggedInBranch(
  searchParams: URLSearchParams,
  signal: AbortSignal,
  onRedirect: (path: string) => void
): Promise<void> {
  const authenticationApi = new AuthenticationApi(authApiConfig_v1, undefined, axiosInstance);
  const { data } = await authenticationApi.getAbilityRules();
  AbacGuardService.getInstance().setAbility(data);

  if (signal.aborted) {
    return;
  }

  const nextUrl = searchParams.get("nextURL") ?? undefined;
  try {
    await externalLoginService.triggerUpgradeFirstEligibility(nextUrl);
  } catch {
    // Swallowed: upgrade eligibility is best-effort and must not block the redirect.
  }

  if (signal.aborted) {
    return;
  }

  const { data: account } = await accountApi.getMe();

  if (!signal.aborted) {
    const nextUrlPath = getSafeNextUrlPath(searchParams);
    const targetPath = resolveTargetPath(account, nextUrlPath);
    onRedirect(targetPath);
  }
}

async function runNonceBranch(
  nonceToken: string,
  searchParams: URLSearchParams,
  signal: AbortSignal,
  onRedirect: (path: string) => void
): Promise<void> {
  await externalLoginService.exchangeNonceToken(nonceToken);

  if (signal.aborted) {
    return;
  }

  const decodedToken = getDecodedUserAccessTokenFromBrowserCookies();
  if (!decodedToken) {
    throw new Error("Decoded token is missing after nonce token exchange");
  }

  const authenticationApi = new AuthenticationApi(authApiConfig_v1, undefined, axiosInstance);
  const { data: abilityRules } = await authenticationApi.getAbilityRules();
  AbacGuardService.getInstance().setAbility(abilityRules);

  if (signal.aborted) {
    return;
  }

  subscribeToQuestionnairePersist(questionnaireSubject$);

  const { data: account } = await accountApi.getMe();

  if (signal.aborted) {
    return;
  }

  try {
    await featureFlag.updateContextWithAccountData({
      ...(decodedToken.account.createdAt && { createdAt: decodedToken.account.createdAt }),
      upgradeFirstEligible: (account as IAccountWithEligibility).upgradeFirstEligible ?? false,
    });
  } catch {
    // Swallowed: feature flag context update is best-effort and must not block the redirect.
  }

  if (signal.aborted) {
    return;
  }

  const nextUrlPath = getSafeNextUrlPath(searchParams);
  const targetPath = resolveTargetPath(account, nextUrlPath);

  // Defer the token store update to the next tick so the navigate (triggered by setRedirectTo)
  // fires and unmounts this guard before useIsLogged() can flip to true and re-trigger the
  // effect down the logged-in branch. Mirrors the same setTimeout used in the original loader.
  setTimeout(function setAuthTokenInNextTick() {
    authenticationServiceInstance.setAuthenticationTokenStore(decodedToken);
  }, 0);

  onRedirect(targetPath);
}

export function useExternalLoginGuard(): IUseExternalLoginGuard {
  const isLogged = useIsLogged();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(() => isLogged || hasNonceToken(searchParams));
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(
    function runExternalLoginGuard() {
      setError(null);
      setRedirectTo(null);

      registerTrackingConsentSync();

      const nonceToken = getNonceTokenFromQueryParams(searchParams);

      if (!isLogged && !nonceToken) {
        setRedirectTo(WELCOME_PATH);
        setLoading(false);
        return;
      }

      setLoading(true);
      const controller = new AbortController();

      function onRedirect(path: string): void {
        setRedirectTo(path);
        setLoading(false);
      }

      function onError(err: unknown): void {
        if (!controller.signal.aborted) {
          setLoading(false);
          setError(err);
        }
      }

      const task = isLogged
        ? runLoggedInBranch(searchParams, controller.signal, onRedirect)
        : runNonceBranch(nonceToken!, searchParams, controller.signal, onRedirect);

      task.catch(onError);

      return function cancelGuardInit() {
        controller.abort();
      };
    },
    [isLogged, searchParams]
  );

  return { loading, redirectTo, error };
}
